import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

const statusStyles = {
  success: "bg-green-100 text-green-800",
  processing: "bg-yellow-100 text-yellow-800",
  failed: "bg-gray-100 text-gray-800",
};

export type Column<Row> = {
  renderHeader: (i: number) => JSX.Element;
  renderRow: (row: Row, rowI: number, colI: number) => JSX.Element;
};

export type Props<Row> = {
  title: string | JSX.Element;
  columns: Column<Row>[];
  rows: Row[];
  setRows?: (rows: Row[]) => void;
  showAddButton?: boolean;
  currentPage: number;
  pageSize: number;
  totalRows: number;
  removePadding?: boolean;
  showPagination?: boolean;
  className?: string;
  onAdd?: () => void;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onRowClick?: (row: Row) => void;
  highlightedRowId?: string;
  setHighlightedRowId?: (id: string | null) => void;
  renderHoverPopup?: (row: Row) => JSX.Element | null;
};

export default function Table<Row>(props: Props<Row>): JSX.Element {
  const { t } = useTranslation();
  const [hoveredRowData, setHoveredRowData] = useState<Row | null>(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());

  const AddButton = () => {
    return (
      <button
        type="button"
        className="grow-0 rounded-full border border-transparent bg-indigo-600 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
        onClick={props.onAdd && props.onAdd}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-8 h-8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>
    );
  };

  useEffect(() => {
    if (!props.renderHoverPopup) return;

    const handleMouseMove = (e: globalThis.MouseEvent) => {
      if (!hoveredRowData) return;

      const rows = Array.from(rowRefs.current.values());
      for (const row of rows) {
        const rect = row.getBoundingClientRect();
        if (
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom
        ) {
          return;
        }
      }

      setHoveredRowData(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [props.renderHoverPopup, hoveredRowData]);

  const Rows = () => {
    rowRefs.current.clear();

    return (
      <tbody className="divide-y divide-gray-200 bg-white">
        {props.rows.length > 0 ? (
          props.rows.map((row: any, i) => (
            <tr
              key={i}
              ref={(el) => el && props.renderHoverPopup && rowRefs.current.set(i, el)}
              onClick={() => props.onRowClick?.(row)}
              onMouseOver={(e) => {
                if (props.renderHoverPopup) {
                  setHoveredRowData(row);
                  // Position popup above the mouse cursor instead of below
                  setPopupPosition({ top: e.clientY - 200, left: e.clientX + 20 });
                }
              }}
              className={`${props.highlightedRowId === row._id ? 'bg-yellow-100' : 'bg-white'} cursor-pointer hover:bg-gray-100`}
            >
              {props.columns.map((column, j) => column.renderRow(row, i, j))}
            </tr>
          ))
        ) : (
          <tr>
            <td
              className="bg-white px-6 py-4 text-sm font-semibold text-gray-900 text-center"
              colSpan={props.columns.length}
            >
              {t('table.noDataFound')}
            </td>
          </tr>
        )}
      </tbody>
    );
  };

  const Navigation = () => {
    return (
      <nav
        className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-2 sm:px-6"
        aria-label="Pagination"
      >
        <div className="hidden sm:block">
          <p className="text-xs text-gray-700">
            {t('table.showing')}{" "}
            <span className="font-medium">
              {props.totalRows > 0
                ? props.pageSize * (props.currentPage - 1) + 1
                : 0}
            </span>{" "}
            {t('table.to')}{" "}
            <span className="font-medium">
              {props.totalRows < props.pageSize * props.currentPage
                ? props.totalRows
                : props.pageSize * props.currentPage}
            </span>{" "}
            {t('table.of')} <span className="font-medium">{props.totalRows}</span> {t('table.results')}
          </p>
        </div>
        <div className="flex flex-1 justify-between sm:justify-end">
          <button onClick={props.onPreviousPage}>
            <a
              href="#"
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('table.previous')}
            </a>
          </button>
          <button
            onClick={props.onNextPage}
            disabled={props.totalRows < props.pageSize * props.currentPage}
          >
            <a
              href="#"
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('table.next')}
            </a>
          </button>
        </div>
      </nav>
    );
  };

  useEffect(() => {
    if (props.highlightedRowId) {
      const timeoutId = setTimeout(() => {
        props.setHighlightedRowId && props.setHighlightedRowId(null);
      }, 5000);
      
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [props.highlightedRowId]);

  return (
    <main className={`${props.className} flex-1 pb-8 relative`}>
      <div className="pt-3">
        <div className="flex flex-row-reverse mx-auto mt-2 max-w-6xl px-4 text-md font-medium leading-6 text-gray-900 sm:px-6 lg:px-8">
          {props.showAddButton && <AddButton />}
        </div>

        <div className={`flex mx-auto mt-2 max-w-6xl ${props.removePadding ? "" : "px-4 sm:px-6 lg:px-8"} text-md font-bold leading-6 text-gray-900`}>
          <h2 className="grow-2">{props.title}</h2>
        </div>

        <div className="sm:block">
          <div className={`mx-auto max-w-6xl ${props.removePadding ? "" : "px-4 sm:px-6 lg:px-8"}`}>
            <div className="mt-2 flex flex-col">
              <div className="flex-1 min-w-full align-middle shadow sm:rounded-lg">
                <div className="min-h-72 max-h-[80vh] overflow-y-auto overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-white sticky top-0 z-0">
                      <tr>{props.columns.map((column, i) => column.renderHeader(i))}</tr>
                    </thead>
                    <Rows />
                  </table>
                </div>
                {props.showPagination !== false && <Navigation />}
              </div>
            </div>
          </div>
        </div>

        {hoveredRowData && props.renderHoverPopup && (() => {
          const popupContent = props.renderHoverPopup(hoveredRowData);
          return popupContent ? (
            <div
              style={{
                position: 'fixed',
                top: `${popupPosition.top}px`,
                left: `${popupPosition.left}px`,
                zIndex: 50,
              }}
              className="bg-white border border-gray-300 rounded-md shadow-lg p-3 text-xs"
            >
              {popupContent}
            </div>
          ) : null;
        })()}
      </div>
    </main>
  );
}